<div class="chat-thread">
  <!-- role="log" : les lecteurs d'écran annoncent chaque nouvelle bulle (MOKA-363) -->
  <div class="chat-thread__messages" #messagesContainer role="log" aria-live="polite" aria-relevant="additions" aria-label="Conversation avec Koffi">
    @if (messages().length === 0) {
      <div class="chat-thread__empty">
        <app-voice-assistant-avatar [greeting]="greeting()" [state]="koffiState()" />

        @if (briefing()) {
          <p class="chat-thread__briefing">{{ briefing() }}</p>
        }
        <span>Posez une question ou demandez une action, à l'écrit ou à l'oral.</span>

        <div class="chat-thread__quick-actions" data-tour="assistant.overlay.suggestions">
          @for (action of primaryActions(); track action.id) {
            <button
              type="button"
              class="chat-thread__quick-action"
              (click)="onQuickAction(action.id)"
            >
              <ng-icon [name]="action.icon" size="16" />
              <span>{{ action.label }}</span>
            </button>
          }
        </div>
      </div>
    }

    @for (message of messages(); track message.timestamp; let i = $index) {
      <div
        class="chat-row"
        [class.chat-row--user]="message.role === 'user'"
      >
        <!--
          Vignette de Koffi devant ses réponses, masquée sur les messages qui
          suivent immédiatement les siens : répéter l'avatar à chaque ligne
          d'une même prise de parole alourdit la lecture (cf. messageries).
          `chat-row__avatar--ghost` garde la gouttière pour aligner les bulles.
        -->
        @if (message.role === 'assistant') {
          @if (startsAssistantTurn(i)) {
            <app-voice-robot class="chat-row__avatar" [size]="28" [animated]="false" />
          } @else {
            <span class="chat-row__avatar chat-row__avatar--ghost" aria-hidden="true"></span>
          }
        }

        <div
          class="chat-bubble"
          [class.chat-bubble--user]="message.role === 'user'"
          [class.chat-bubble--assistant]="message.role === 'assistant'"
        >
          @if (message.role === 'assistant' && startsAssistantTurn(i)) {
            <div class="chat-bubble__meta">
              <span class="chat-bubble__author">Koffi</span>
              <span class="chat-bubble__time">{{ formatTime(message.timestamp) }}</span>
            </div>
          }

          @if (message.role === 'assistant' && message.confirmation; as card) {
            <div class="chat-confirm" role="group" [attr.aria-label]="card.title">
              <p class="chat-confirm__title">{{ card.title }}</p>
              @for (detail of card.details; track detail) {
                <p class="chat-confirm__detail">{{ detail }}</p>
              }
              @if (card.id === activeConfirmationId()) {
                <div class="chat-confirm__actions">
                  <button
                    type="button"
                    class="chat-confirm__btn chat-confirm__btn--confirm"
                    (click)="onOptionClick(card.confirmOption)"
                  >{{ card.confirmOption.label }}</button>
                  <button
                    type="button"
                    class="chat-confirm__btn"
                    (click)="onOptionClick(card.cancelOption)"
                  >{{ card.cancelOption.label }}</button>
                </div>
              }
            </div>
          } @else {
            <p class="chat-bubble__content">{{ message.content }}</p>
          }

        @if (message.role === 'assistant' && message.links?.length) {
          <div class="chat-bubble__links">
            @for (link of message.links; track link.route) {
              <button
                type="button"
                class="chat-bubble__link-btn"
                (click)="onLinkClick(link)"
              >
                <ng-icon name="iconoirArrowUpRight" size="13" />
                <span>{{ link.label }}</span>
              </button>
            }
          </div>
        }

        @if (message.role === 'assistant' && message.options?.length) {
          <div class="chat-bubble__options">
            @for (option of message.options; track option.id) {
              <button
                type="button"
                class="chat-bubble__option-btn"
                [class.chat-bubble__option-btn--emphasis]="option.emphasis"
                (click)="onOptionClick(option)"
              >
                {{ option.label }}
                @if (option.hint) {
                  <span class="chat-bubble__option-hint">{{ option.hint }}</span>
                }
              </button>
            }
          </div>
        }

          @if (message.role === 'user') {
            <span class="chat-bubble__time chat-bubble__time--user">{{ formatTime(message.timestamp) }}</span>
          }
        </div>
      </div>
    }

    @if (isRecording() && liveTranscript()) {
      <div class="chat-bubble chat-bubble--user chat-bubble--pending">
        <p class="chat-bubble__content">{{ liveTranscript() }}</p>
      </div>
    }

    @if (isProcessing()) {
      <div class="chat-thread__status" role="status">
        <span class="chat-thread__spinner"></span>
        <span>Analyse de votre demande…</span>
      </div>
    }

    @if (hasError() && error()) {
      <div class="chat-thread__status chat-thread__status--error" role="alert">
        <ng-icon name="iconoirWarningTriangle" size="16" aria-hidden="true" />
        <span>{{ error() }}</span>
      </div>
    }

    <!-- La dernière demande écrite n'est pas passée : on la renvoie telle quelle (MOKA-363). -->
    @if (canRetry()) {
      <div class="chat-thread__retry-row">
        <span>Votre demande n'a pas abouti.</span>
        <button type="button" class="chat-thread__retry" (click)="retry.emit()">
          <ng-icon name="iconoirRefreshDouble" size="14" aria-hidden="true" />
          Réessayer
        </button>
      </div>
    }

    @if (pendingFollowUp()) {
      <div class="chat-thread__followup">
        <span>Une question est en attente de réponse.</span>
      </div>
    }
  </div>


  <div class="chat-thread__composer" data-tour="assistant.overlay.composer">
    @if (slashPaletteOpen()) {
      <app-slash-command-palette
        [filterText]="draft().slice(1)"
        [activeIndex]="paletteActiveIndex()"
        (select)="onQuickAction($event)"
        (dismiss)="closeSlashPalette()"
      />
    }

    <!-- Bouton micro désactivé pendant la lecture TTS (Lot E, addendum) :
         démarrer un enregistrement pendant que l'assistant parle capterait sa
         propre voix. Il redevient cliquable dès que `isSpeaking()` retombe. -->
    <textarea
      #draftInput
      class="chat-thread__textarea"
      rows="1"
      placeholder="Écrivez votre demande… (« / » pour les actions rapides)"
      [value]="draft()"
      (input)="onInput($event)"
      (keydown)="onKeydown($event)"
    ></textarea>

    <button
      type="button"
      class="chat-thread__mic-btn"
      data-tour="assistant.overlay.mic"
      [class.chat-thread__mic-btn--active]="isRecording()"
      [class.chat-thread__mic-btn--speaking]="isSpeaking()"
      [disabled]="isSpeaking()"
      (click)="onMicClick()"
      [attr.aria-label]="isRecording() ? 'Arrêter l\'enregistrement' : (isSpeaking() ? 'Lecture en cours...' : 'Démarrer la commande vocale')"
      [attr.title]="isRecording() ? 'Arrêter l\'enregistrement' : (isSpeaking() ? 'Lecture en cours...' : 'Démarrer la commande vocale')"
    >
      <ng-icon [name]="isRecording() ? 'iconoirSquare' : 'iconoirMicrophone'" size="18" />
    </button>

    <button
      type="button"
      class="chat-thread__send-btn"
      [disabled]="!draft().trim()"
      (click)="onSubmit()"
      aria-label="Envoyer"
      title="Envoyer"
    >
      <ng-icon name="iconoirSendDiagonal" size="18" />
    </button>
  </div>
</div>
